import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Popover, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { numbersToStringWithCommas } from '../helpers';
import { ChartTooltip } from './ChartTooltip';
import {
  CustomEventChartBase,
  DatasetResponse,
} from 'src/services/insight-cards/types';

interface MetricChartProps {
  chart: CustomEventChartBase;
  dataset: DatasetResponse | undefined;
  endTimestamp: number;
  isChartNameVisible: boolean;
  startTimestamp: number;
  titleFontSize: string;
}

export default function MetricChart({
  chart,
  dataset,
  endTimestamp,
  isChartNameVisible,
  startTimestamp,
  titleFontSize,
}: MetricChartProps) {
  const theme = useTheme();
  const { t } = useTranslation('solve-insights');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const series = useMemo(() => {
    return (
      dataset?.is_grouped ? dataset.series.flat() : dataset?.series || []
    ).map(item => ({
      aggregationLabel: item.aggregation_label,
      data: item.data,
      eventName: item.event_name,
      label: item.label,
      percentage: item.percentage,
      rawData: item.data,
      sourceItems: item.source_items,
    }));
  }, [dataset]);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setHoveredIndex(null);
  };

  const open = Boolean(anchorEl) && hoveredIndex !== null;

  return (
    <Box
      sx={{
        '> .MuiTypography-root': {
          fontSize: titleFontSize,
        },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: `4px 8px ${titleFontSize}`,

        width: '100%',
      }}
    >
      {isChartNameVisible && (
        <Typography variant='font16Bold'>{chart.name}</Typography>
      )}
      <Box
        sx={{
          containerType: 'inline-size',
          display: 'grid',
          gap: series.length === 5 ? 1 : 2,
          gridTemplateColumns: (() => {
            if (series.length === 1) {
              return '1fr';
            }
            if (series.length === 2) {
              return '1fr 1fr';
            }
            if (series.length === 3 || series.length === 4) {
              return '1fr 1fr';
            }
            if (series.length === 5) {
              return '1fr 1fr';
            }
            return '1fr 1fr';
          })(),

          gridTemplateRows: (() => {
            if (series.length === 1) {
              return '1fr';
            }
            if (series.length === 2) {
              return '1fr';
            }
            if (series.length === 3 || series.length === 4) {
              return '1fr 1fr';
            }
            if (series.length === 5) {
              return '1fr 1fr 1fr';
            }
            return '1fr 1fr';
          })(),

          height: '100%',

          overflowY: 'auto',
        }}
      >
        {series.map(({ data, label }, index) => {
          const prefix =
            dataset?.tooltip?.value_prefix ||
            (dataset?.variant_metadata ? (data > 0 ? '+' : '') : '');
          const suffix = dataset?.tooltip?.value_suffix || '';
          const formatted =
            prefix +
            numbersToStringWithCommas({ canBeFloat: true, number: data }) +
            suffix;

          return (
            <Box
              key={index}
              onMouseEnter={event => handleMouseEnter(event, index)}
              onMouseLeave={handleMouseLeave}
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: series.length === 5 ? 0 : 'clamp(8px, 2cqh, 32px)',
                justifyContent: 'center',

                ...(series.length === 5 &&
                  index === 4 && {
                    gridColumn: '1 / -1',
                    gridRow: '3',
                  }),

                '.MuiTypography-root:first-of-type': {
                  fontSize:
                    series.length === 5
                      ? 'clamp(16px, 5cqw, 32px)'
                      : 'clamp(16px, 6cqw, 64px)',
                },

                '.MuiTypography-root:last-of-type': {
                  fontSize: 'clamp(16px, 2cqw, 32px)',
                },

                cursor: 'pointer',
              }}
            >
              <Typography
                color={
                  dataset?.variant_metadata
                    ? data === 0
                      ? theme.palette.text.primary
                      : data > 0
                        ? theme.palette.colors.green[500]
                        : theme.palette.colors.red[500]
                    : undefined
                }
                variant='font42'
              >
                {formatted}
              </Typography>
              <Box
                alignItems='center'
                component={'span'}
                display='flex'
                gap={'10px'}
              >
                {dataset?.variant_metadata && (
                  <Box
                    sx={{
                      bgcolor: theme.palette.chart[index + 1],
                      height: '12px',
                      width: '12px',
                    }}
                  ></Box>
                )}
                <Typography variant='font16'>{label}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        disableRestoreFocus
        onClose={handleMouseLeave}
        open={open}
        sx={{
          pointerEvents: 'none',
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
      >
        {hoveredIndex !== null && (
          <ChartTooltip
            aggregationLabel={
              series[hoveredIndex]?.aggregationLabel || undefined
            }
            aggregationType={chart.aggregation_type}
            chartType={chart.chart_type}
            endTimestamp={endTimestamp}
            eventName={series[hoveredIndex]?.eventName}
            isGrouped={dataset?.is_grouped}
            label={series[hoveredIndex]?.label || ''}
            percentage={series[hoveredIndex]?.percentage ?? null}
            sourceItems={series[hoveredIndex]?.sourceItems || null}
            startTimestamp={startTimestamp}
            t={t}
            theme={theme}
            totalSum={dataset?.total_sum}
            value={series[hoveredIndex]?.rawData || 0}
            valuePrefix={dataset?.tooltip?.value_prefix}
            valueSuffix={dataset?.tooltip?.value_suffix}
          />
        )}
      </Popover>
    </Box>
  );
}
